import {
  CFormCheck,
  CFormInput,
  CModal,
  CModalBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useToast } from "../../hooks/useToast";
import { useCookie } from "../../hooks/useCookie";
import { useQueryClient } from "react-query";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import PromptModal from "../../components/modals/PromptModal";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import { faCheck, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface newSuggestedMessageModalProps {
  open: boolean;
  onClose: () => void;
  id: string;
  suggestions: Suggestions;
  mode: "create" | "update";
}

interface updateSuggestedMessageModalProps {
  id: string;

  suggestion: {
    fr: string;
    en: string;
    ar: string;
  };
  isForDriver: boolean;
  isForUser: boolean;
}

export const SuggestedMessageModal = ({
  open,
  onClose,
  id,
  suggestions,
  mode,
}: newSuggestedMessageModalProps) => {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();

  const [forms, setForms] = useState<
    Array<{
      suggestion: string;
      arabicTranslation: string;
      englishTranslation: string;
      isForDriver: boolean;
      isForUser: boolean;
      isNew: boolean;
    }>
  >([
    {
      suggestion: suggestions.suggestion.fr,
      arabicTranslation: suggestions.suggestion.ar,
      englishTranslation: suggestions.suggestion.en,
      isForDriver: suggestions.isForDriver,
      isForUser: suggestions.isForUser,
      isNew: false,
    },
  ]);

  useEffect(() => {
    if (open) {
      setForms([
        {
          suggestion: suggestions.suggestion.fr,
          arabicTranslation: suggestions.suggestion.ar,
          englishTranslation: suggestions.suggestion.en,
          isForDriver: suggestions.isForDriver,
          isForUser: suggestions.isForUser,
          isNew: false,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateSuggestedMessageMutation = useMutation(
    (data: updateSuggestedMessageModalProps) => {
      return apiService.MakePutRequest(
        `rides/chat-suggestion/${data.id}`,
        data,
        token,
      );
    },
  );

  type CreatePayload = {
    suggestions: Array<{
      suggestion: {
        fr: string;
        en: string;
        ar: string;
      };
      isForDriver: boolean;
      isForUser: boolean;
    }>;
  };

  const createSuggestedMessageMutation = useMutation((data: CreatePayload) => {
    return apiService.MakePostRequest(`rides/chat-suggestion`, data, token);
  });

  const isLoading =
    updateSuggestedMessageMutation.isLoading ||
    createSuggestedMessageMutation.isLoading;

  const validateRow = (row: {
    suggestion: string;
    arabicTranslation: string;
    englishTranslation: string;
  }) => !!row.suggestion && row.suggestion.trim().length > 0;

  const onSave = () => {
    if (mode === "update") {
      const row = forms[0];
      if (!validateRow(row)) {
        toastError(general.fr.message.operationFailed);
        return;
      }
      updateSuggestedMessageMutation.mutate(
        {
          id,
          suggestion: {
            fr: row.suggestion.trim(),
            en: row.englishTranslation.trim(),
            ar: row.arabicTranslation.trim(),
          },
          isForDriver: row.isForDriver,
          isForUser: row.isForUser,
        },
        {
          onError() {
            toastError(general.fr.message.operationFailed);
          },
          onSuccess() {
            toastSuccess(general.fr.message.chatSuggestionUpdateded);
            queryClient.invalidateQueries("getAllChatSuggestions");
            onClose();
          },
        },
      );
      return;
    }
    // create mode - UPDATED FORMAT
    const rowsToCreate = forms;
    if (rowsToCreate.some((r) => !validateRow(r))) {
      toastError(general.fr.message.operationFailed);
      return;
    }

    // Format data according to API expectations
    const suggestionsPayload = {
      suggestions: rowsToCreate.map((r) => ({
        suggestion: {
          fr: r.suggestion.trim(),
          en: r.englishTranslation.trim(),
          ar: r.arabicTranslation.trim(),
        },
        isForDriver: r.isForDriver,
        isForUser: r.isForUser,
      })),
    };

    createSuggestedMessageMutation.mutate(suggestionsPayload, {
      onError() {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.chatSuggestionUpdateded);
        queryClient.invalidateQueries("getAllChatSuggestions");
        onClose();
      },
    });
  };

  const onChangeText =
    (
      index: number,
      key: "suggestion" | "arabicTranslation" | "englishTranslation",
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForms((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [key]: value };
        return next;
      });
    };

  const onChangeCheck =
    (index: number, key: "isForDriver" | "isForUser") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.checked;
      setForms((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [key]: value };
        return next;
      });
    };

  const canAddRows = mode === "create";

  const appendException = () => {
    if (!canAddRows) return;
    setForms((prev) => [
      ...prev,
      {
        suggestion: "".trim(),
        arabicTranslation: "".trim(),
        englishTranslation: "".trim(),
        isForDriver: false,
        isForUser: false,
        isNew: true,
      },
    ]);
  };

  const removeSuggestedMessage = (index: number) => {
    if (!canAddRows) return;
    setForms((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <CModal
      visible={open}
      onClose={onClose}
      alignment="center"
      size="xl"
      backdrop="static"
    >
      <CModalBody style={{ minHeight: "200px" }}>
        {forms.map((form, index) => (
          <div
            key={index}
            className="d-flex justify-content-between border-bottom py-3"
          >
            <div className="px-2" style={{ flex: 1 }}>
              <label htmlFor="suggestion">FR Message</label>
              <CFormInput
                name={`suggestion_${index}`}
                id={`suggestion_${index}`}
                defaultValue={forms[index].suggestion}
                onChange={onChangeText(index, "suggestion")}
                disabled={isLoading}
              />
            </div>
            <div className="px-2" style={{ flex: 1 }}>
              <label htmlFor="arabicTranslation">AR Message</label>
              <CFormInput
                name={`arabicTranslation_${index}`}
                id={`arabicTranslation_${index}`}
                defaultValue={forms[index].arabicTranslation}
                onChange={onChangeText(index, "arabicTranslation")}
                disabled={isLoading}
              />
            </div>
            <div className="px-2" style={{ flex: 1 }}>
              <label htmlFor="englishTranslation">EN Message</label>
              <CFormInput
                name={`englishTranslation_${index}`}
                id={`englishTranslation_${index}`}
                defaultValue={forms[index].englishTranslation}
                onChange={onChangeText(index, "englishTranslation")}
                disabled={isLoading}
              />
            </div>
            <div
              className="px-2 d-flex flex-column justify-content-end"
              style={{ flex: 0.7 }}
            >
              <CFormCheck
                id={`isForDriver_${index}`}
                label="Pour le chauffeur"
                checked={form.isForDriver}
                onChange={onChangeCheck(index, "isForDriver")}
                disabled={isLoading}
              />
              <CFormCheck
                id={`isForUser_${index}`}
                label="Pour l'utilisateur"
                className="mt-2"
                checked={form.isForUser}
                onChange={onChangeCheck(index, "isForUser")}
                disabled={isLoading}
              />
            </div>
            <div
              className="d-flex align-items-end justify-content-end"
              style={{ width: "45px" }}
            >
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={
                  canAddRows && forms.length > 1
                    ? () => removeSuggestedMessage(index)
                    : undefined
                }
                disabled={!canAddRows || (forms.length < 1 && form.isNew)}
                style={{
                  width: "41px",
                  height: "41px",
                  opacity:
                    canAddRows && forms.length < 1 && form.isNew ? 0.4 : 1,
                  cursor:
                    canAddRows && forms.length < 1 && form.isNew
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <DeleteIcon width="18" height="18" fill="#ffffff" />
              </button>
            </div>
          </div>
        ))}

        <div className="d-flex justify-content-center mt-4">
          <button
            type="button"
            className="btn btn-primary btn-sm text-white"
            onClick={appendException}
            disabled={isLoading || !canAddRows}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </CModalBody>
      <div className="d-flex justify-content-center gap-4 p-4">
        <button
          type="button"
          className="btn btn-primary btn-sm text-white"
          onClick={onClose}
          disabled={isLoading}
        >
          Annuler
        </button>
        <button
          type="button"
          className="btn btn-danger btn-sm text-white"
          onClick={onSave}
          disabled={isLoading}
        >
          {isLoading ? <CSpinner size="sm" /> : "Enregistrer"}
        </button>
      </div>
    </CModal>
  );
};

function SuggestionsMessageBodyRowContent({
  suggestionsMessage,
  deletedList,
}: {
  suggestionsMessage: SuggestedMessage;
  deletedList: React.Dispatch<BulkDeleteActions>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);

  const deleteSuggestedMessage = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`rides/chat-suggestion/${id}`, token);
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: suggestionsMessage.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: suggestionsMessage.id });
    }
  }, [deletedList, isChecked, suggestionsMessage.id]);

  const removeSuggestedMessage = () => {
    deleteSuggestedMessage.mutate(suggestionsMessage.id, {
      onError(error) {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.chatSuggestionDeleted);
        queryClient.invalidateQueries("getAllChatSuggestions");
      },
    });
  };

  return (
    <CTableRow
      style={{
        borderLeft: isChecked ? `3px solid ${colors.primary}` : "",
        width: "100%",
        position: "relative",
      }}
    >
      <CTableDataCell className=" text-capitalize text-sm">
        <CFormCheck
          id={`${suggestionsMessage.id}`}
          label=""
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
          color={colors.yellow}
        />
        <span className="ms-3">{suggestionsMessage?.suggestion}</span>
      </CTableDataCell>
      <CTableDataCell className="text-capitalize text-sm">
        <span className="ms-3">{suggestionsMessage?.arabicTranslation}</span>
      </CTableDataCell>
      <CTableDataCell className="text-capitalize text-sm">
        <span className="ms-3">{suggestionsMessage?.englishTranslation}</span>
      </CTableDataCell>
      <CTableDataCell className="text-capitalize text-sm">
        <span className="ms-3">
          {suggestionsMessage?.isForDriver ? (
            <FontAwesomeIcon icon={faCheck} color={colors.green} size="xl" />
          ) : (
            <FontAwesomeIcon icon={faXmark} color={colors.red} size="xl" />
          )}
        </span>
      </CTableDataCell>
      <CTableDataCell className="text-capitalize text-sm">
        <span className="ms-3">
          {suggestionsMessage?.isForUser ? (
            <FontAwesomeIcon icon={faCheck} color={colors.green} size="xl" />
          ) : (
            <FontAwesomeIcon icon={faXmark} color={colors.red} size="xl" />
          )}
        </span>
      </CTableDataCell>

      <CTableDataCell>
        <button
          onClick={() => setShowEditModal(true)}
          className="outline-none border-0 bg-transparent"
        >
          <EditIcon width="18" height="18" fill={colors.secondary} />
        </button>{" "}
        <button
          className="outline-none border-0 bg-transparent"
          onClick={() => setShowModal(true)}
        >
          {deleteSuggestedMessage.isLoading ? (
            <CSpinner size="sm" />
          ) : (
            <DeleteIcon
              width="20"
              height="20"
              fill={colors.primary}
              className="ms-2"
            />
          )}
        </button>
      </CTableDataCell>
      <PromptModal
        open={showModal}
        onAccept={removeSuggestedMessage}
        onClose={() => setShowModal(false)}
      />
      <SuggestedMessageModal
        onClose={() => setShowEditModal(false)}
        open={showEditModal}
        suggestions={{
          suggestion: {
            fr: suggestionsMessage.suggestion,
            en: suggestionsMessage.englishTranslation,
            ar: suggestionsMessage.arabicTranslation,
          },
          isForDriver: suggestionsMessage.isForDriver,
          isForUser: suggestionsMessage.isForUser,
        }}
        id={suggestionsMessage.id}
        mode="update"
      />
    </CTableRow>
  );
}

interface TableProps {
  suggestionsMessage: SuggestedMessage[];
  suggestionsMessageToBeDeleted: React.Dispatch<BulkDeleteActions>;
}

export default function SuggestionsMessageTable({
  suggestionsMessage,
  suggestionsMessageToBeDeleted,
}: TableProps) {
  return (
    <div className="mt-3">
      <CTable>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col">FR Message</CTableHeaderCell>
            <CTableHeaderCell scope="col">AR Message</CTableHeaderCell>
            <CTableHeaderCell scope="col">EN Message</CTableHeaderCell>
            <CTableHeaderCell scope="col">Pour le chauffeur</CTableHeaderCell>
            <CTableHeaderCell scope="col">Pour l'utilisateur</CTableHeaderCell>
            <CTableHeaderCell scope="col"></CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {suggestionsMessage?.map((suggestionsMessage) => (
            <SuggestionsMessageBodyRowContent
              suggestionsMessage={suggestionsMessage}
              key={suggestionsMessage?.id}
              deletedList={suggestionsMessageToBeDeleted}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
